import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Section, Chip } from "@/growth/components/_ui";
import * as gc from "@/growth/api";

export default function CreativeTab({ wsId, onChanged }) {
  const [briefs, setBriefs] = useState([]);
  const [activeBriefId, setActiveBriefId] = useState(null);
  const [concepts, setConcepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [form, setForm] = useState({
    goal: "", audience: "", channel: "social",
    message_angle: "", hypothesis: "", primary_cta: "Learn more", include_image: true,
  });

  const loadBriefs = async () => {
    const r = await gc.listBriefs(wsId);
    setBriefs(r.items || []);
  };
  const loadConcepts = async (id) => {
    const r = await gc.listConcepts(id);
    setConcepts(r.items || []);
  };
  useEffect(() => { loadBriefs(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeBriefId) loadConcepts(activeBriefId); }, [activeBriefId]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const doCreate = async () => {
    if (!form.goal || !form.audience) { toast.error("Goal and audience are required"); return; }
    setBusy(true);
    try {
      const r = await gc.createBrief({ workspace_id: wsId, ...form });
      toast.success("Brief saved. Generating 3 concept variations…");
      const g = await gc.generateConcepts(r.brief_id);
      setActiveBriefId(r.brief_id);
      setConcepts(g.concepts || []);
      await loadBriefs();
      onChanged?.();
    } catch { toast.error("Generate failed"); }
    finally { setBusy(false); }
  };

  const doApproveConcept = async (conceptId) => {
    setApprovingId(conceptId);
    try {
      await gc.approveConcept(conceptId);
      toast.success("Concept approved. It's now the winner in the Approvals tab.");
      if (activeBriefId) await loadConcepts(activeBriefId);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div>
      <Section title="Creative Studio" subtitle="Every brief produces three MEANINGFULLY different concepts. Each one carries a hypothesis you can test — approve the winner to log it in the Approvals queue.">
        <Card className="p-5 border border-slate-200 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Goal</Label>
              <Input value={form.goal} onChange={(e) => set("goal", e.target.value)} placeholder="e.g. Book 20 estimate calls in 30 days" className="mt-1" data-testid="gc-brief-goal" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Audience</Label>
              <Input value={form.audience} onChange={(e) => set("audience", e.target.value)} placeholder="e.g. Homeowners in Austin, 35-55" className="mt-1" data-testid="gc-brief-audience" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Channel</Label>
              <select value={form.channel} onChange={(e) => set("channel", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="gc-brief-channel">
                <option value="social">Social post</option>
                <option value="search-ad">Search ad</option>
                <option value="landing">Landing hero</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Primary CTA</Label>
              <Input value={form.primary_cta} onChange={(e) => set("primary_cta", e.target.value)} className="mt-1" data-testid="gc-brief-cta" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Hypothesis (optional)</Label>
              <Textarea value={form.hypothesis} onChange={(e) => set("hypothesis", e.target.value)} rows={2} placeholder="What are you trying to prove or learn?" className="mt-1" data-testid="gc-brief-hypothesis" />
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={form.include_image} onChange={(e) => set("include_image", e.target.checked)} />
            <span>Include AI-generated visual mock (via Nano Banana)</span>
          </label>
          <div className="mt-4">
            <Button onClick={doCreate} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="gc-brief-generate">
              {busy ? "Generating…" : "Generate 3 concepts"}
            </Button>
          </div>
        </Card>
      </Section>

      {briefs.length > 0 && (
        <Section title="Recent briefs" subtitle="Select a brief to inspect its concept variations.">
          <div className="flex flex-wrap gap-2 mb-4">
            {briefs.map((b) => (
              <button
                key={b.id}
                onClick={() => setActiveBriefId(b.id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${activeBriefId === b.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                data-testid={`gc-brief-chip-${b.id}`}
              >
                {b.goal.slice(0, 40)}{b.goal.length > 40 ? "…" : ""}
              </button>
            ))}
          </div>
        </Section>
      )}

      {concepts.length > 0 && (
        <Section title="Concept variations" subtitle="Approve one and it becomes the winning hypothesis in the Approvals tab.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {concepts.map((c) => {
              const isApproved = c.status === "approved";
              return (
                <Card key={c.id} className={`p-5 border rounded-2xl flex flex-col ${isApproved ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`gc-concept-${c.id}`}>
                  {c.image_data_url && (
                    <img src={c.image_data_url} alt="concept" className="rounded-lg mb-3 w-full h-40 object-cover border border-slate-200" />
                  )}
                  <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">{c.angle}</div>
                  <div className="font-bold text-slate-900 text-lg mt-1">{c.headline}</div>
                  <div className="text-sm text-slate-600 mt-2">{c.body}</div>
                  <div className="mt-3 text-xs text-slate-500"><strong>CTA:</strong> {c.cta}</div>
                  <div className="mt-1 text-xs text-slate-500"><strong>Visual:</strong> {c.visual_direction}</div>
                  <div className="mt-2 text-xs italic text-slate-500">Hypothesis: {c.hypothesis}</div>
                  {c.forbidden_check?.length > 0 && (
                    <div className="mt-3 text-xs text-rose-700"><strong>⚠ Flags:</strong> {c.forbidden_check.join("; ")}</div>
                  )}
                  <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                    <Chip tone={isApproved ? "emerald" : "amber"}>{isApproved ? "Approved · winner" : "Draft · needs approval"}</Chip>
                    {!isApproved && (
                      <Button
                        size="sm"
                        onClick={() => doApproveConcept(c.id)}
                        disabled={approvingId === c.id}
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
                        data-testid={`gc-concept-approve-${c.id}`}
                      >
                        {approvingId === c.id ? "Approving…" : "Approve"}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}
